/**
 * Six stationery-mecha bosses. Each has a cached static hull and animated
 * limbs (tentacles, saws, flags, steam) plus phase-coloured cores and damage
 * smoke. Bosses face down towards the squad.
 */
import type { Enemy } from "../types";
import {
  INK,
  TAU,
  box,
  clamp,
  disc,
  glow,
  hash,
  line,
  linear,
  oval,
  poly,
  radial,
  shade,
  silhouette,
  stamp,
} from "./kit";
import { DANGER } from "./enemies";

type Ctx = CanvasRenderingContext2D;
const W = 3;
/** Attack phase colours (engine switches phase every 300 ticks). */
const PHASE = ["#FF9A3D", "#FF3D8B", "#B36BFF"];

function lit(c: Ctx, r: number, base: string, spread = 0.24) {
  return linear(c, -r, -r, r, r, [
    [0, shade(base, spread)],
    [0.55, base],
    [1, shade(base, -spread)],
  ]);
}
function bigEye(c: Ctx, x: number, y: number, r: number, color: string, lid = 0) {
  disc(c, x, y, r * 1.2, INK);
  disc(c, x, y, r, "#FFF7EC");
  disc(c, x, y + r * 0.1, r * 0.62, radial(c, x, y + r * 0.1, 0, r * 0.62, [[0, "#FFFFFF"], [0.3, shade(color, 0.3)], [1, color]]));
  disc(c, x, y + r * 0.1, r * 0.24, INK);
  disc(c, x - r * 0.3, y - r * 0.25, r * 0.2, "#FFFFFF");
  // Angry brow: the lid drops towards the inner corner (lid sign = eye side).
  if (lid) {
    const outer = x + Math.sign(lid) * r * 1.25,
      inner = x - Math.sign(lid) * r * 1.25;
    poly(c, [[outer, y - r * 1.3], [inner, y - r * 1.3], [inner, y - r * (0.35 - Math.abs(lid))], [outer, y - r * 0.8]], INK, null);
  }
}

interface BossDesign {
  span: [number, number];
  body(c: Ctx, r: number): void;
  under?(c: Ctx, e: Enemy, r: number, t: number, phase: string): void;
  over?(c: Ctx, e: Enemy, r: number, t: number, phase: string): void;
  /** Damage smoke anchors, in radii. */
  vents: number[][];
}

const BOSSES: BossDesign[] = [
  // 0 纸翼轰炸机 — folded paper flying wing with four engines.
  {
    span: [3.8, 2.2],
    vents: [[-0.9, -0.2], [0.6, -0.1], [0.1, -0.3]],
    under(c, e, r, t) {
      for (const x of [-1.0, -0.5, 0.5, 1.0]) {
        const f = 0.8 + 0.2 * Math.sin(t * 0.7 + x * 5);
        glow(c, x * r, -r * 0.62, r * 0.32 * f, "#FF9A3D", 0.9);
        glow(c, x * r, -r * 0.62, r * 0.16 * f, "#FFF0C0", 1);
      }
    },
    over(c, e, r, t, phase) {
      const on = Math.floor(t / 20) % 2 === 0;
      disc(c, -r * 1.7, -r * 0.14, r * 0.05, on ? "#FF4D6A" : "#5A2230");
      disc(c, r * 1.7, -r * 0.14, r * 0.05, on ? "#6DFF9E" : "#1E4A2E");
      for (let i = -2; i <= 2; i++) glow(c, i * r * 0.1, r * 0.5, r * 0.08, phase, 0.8);
    },
    body(c, r) {
      for (const s of [-1, 1]) {
        const tip = [s * r * 1.78, -r * 0.14];
        poly(c, [[0, r * 0.78], tip, [s * r * 1.5, -r * 0.46], [s * r * 1.05, -r * 0.26], [s * r * 0.72, -r * 0.56], [s * r * 0.36, -r * 0.36], [0, -r * 0.62]], s < 0 ? "#EFE7D5" : "#E2D8C2", INK, W);
        poly(c, [[0, r * 0.78], tip, [s * r * 0.9, -r * 0.05]], s < 0 ? "#FBF6EA" : "#F2EBDC", null);
        line(c, [[0, r * 0.78], tip], "rgba(120,100,80,0.45)", 1.5);
        line(c, [[s * r * 0.3, r * 0.2], [s * r * 1.2, -r * 0.24]], "rgba(120,100,80,0.35)", 1.2);
        poly(c, [tip, [s * r * 1.5, -r * 0.46], [s * r * 1.38, -r * 0.2]], DANGER, INK, 2);
        for (const x of [0.5, 1.0]) {
          box(c, s * x * r - r * 0.13, -r * 0.62, r * 0.26, r * 0.5, r * 0.1, lit(c, r * 0.3, "#3A4666"), INK, 2.4);
          oval(c, s * x * r, -r * 0.6, r * 0.09, r * 0.04, "#1A1E2C");
        }
      }
      poly(c, [[-r * 0.3, r * 0.45], [r * 0.3, r * 0.45], [r * 0.22, -r * 0.45], [-r * 0.22, -r * 0.45]], lit(c, r * 0.5, "#46537A"), INK, W);
      c.save();
      c.beginPath();
      c.rect(-r * 0.16, -r * 0.3, r * 0.32, r * 0.5);
      c.clip();
      for (let i = -6; i < 8; i++) line(c, [[-r * 0.3, i * r * 0.08], [r * 0.3, i * r * 0.08 + r * 0.2]], i % 2 ? "#FFC94D" : "#2A2F3E", r * 0.04, "butt");
      c.restore();
      c.strokeStyle = INK;
      c.lineWidth = 2;
      c.strokeRect(-r * 0.16, -r * 0.3, r * 0.32, r * 0.5);
      box(c, -r * 0.24, r * 0.43, r * 0.48, r * 0.14, r * 0.07, "#1A1E2C", INK, 2);
    },
  },
  // 1 墨水章鱼 — glossy ink octopus with curling tentacles.
  {
    span: [2.4, 2.6],
    vents: [[-0.4, -0.6], [0.35, -0.45], [0, -0.9]],
    under(c, e, r, t) {
      for (let i = 0; i < 8; i++) {
        const base = Math.PI * (0.18 + (i / 7) * 0.64),
          pts: number[][] = [];
        for (let k = 0; k <= 12; k++) {
          const a = k / 12,
            ang = base + Math.sin(t * 0.06 + i * 1.3 + a * 3) * 0.5 * a;
          pts.push([Math.cos(base) * r * 0.4 + Math.cos(ang) * a * r * 1.35, r * 0.3 + Math.sin(ang) * a * r * 1.35]);
        }
        for (let k = 0; k < 12; k++) line(c, [pts[k], pts[k + 1]], INK, r * 0.28 * (1 - k / 13) + 4);
        for (let k = 0; k < 12; k++) line(c, [pts[k], pts[k + 1]], i % 2 ? "#4B3B94" : "#3D2F80", r * 0.28 * (1 - k / 13));
        for (let k = 2; k < 11; k += 2) disc(c, pts[k][0], pts[k][1], r * 0.045 * (1 - k / 14) + 1, "#C9A9FF");
      }
    },
    over(c, e, r, t, phase) {
      const blink = t % 180 < 8 ? 0.9 : 0;
      for (const s of [-1, 1]) {
        glow(c, s * r * 0.36, r * 0.12, r * 0.35, phase, 0.55);
        if (blink) poly(c, [[s * r * 0.6, -r * 0.1], [s * r * 0.12, -r * 0.1], [s * r * 0.12, r * 0.35], [s * r * 0.6, r * 0.35]], "#2A1F5C", null);
      }
      for (let i = 0; i < 3; i++) {
        const y = r * 0.4 + ((t * 0.5 + i * 25) % (r * 0.8));
        disc(c, (i - 1) * r * 0.25, y, r * 0.05, "rgba(40,20,90,0.8)");
      }
    },
    body(c, r) {
      oval(c, 0, -r * 0.3, r * 0.9, r * 1.02, linear(c, -r, -r * 1.3, r, r * 0.6, [[0, "#6A57C6"], [0.5, "#3B2E7A"], [1, "#1B1440"]]), INK, W);
      for (let i = 0; i < 7; i++) oval(c, (hash(i, 1) - 0.5) * r * 1.2, -r * 0.3 - hash(i, 2) * r * 0.9, r * (0.06 + hash(i, 3) * 0.08), r * (0.05 + hash(i, 3) * 0.06), "rgba(170,140,255,0.35)");
      oval(c, -r * 0.35, -r * 0.85, r * 0.3, r * 0.16, "rgba(255,255,255,0.45)", null, 0, -0.5);
      oval(c, -r * 0.5, -r * 0.62, r * 0.08, r * 0.05, "rgba(255,255,255,0.6)");
      for (const s of [-1, 1]) bigEye(c, s * r * 0.36, r * 0.12, r * 0.22, "#FF3D8B", s * 0.25);
      c.beginPath();
      c.moveTo(-r * 0.18, r * 0.5);
      c.quadraticCurveTo(0, r * 0.4, r * 0.18, r * 0.5);
      c.strokeStyle = INK;
      c.lineWidth = 3;
      c.stroke();
    },
  },
  // 2 折纸堡垒 — flying origami castle with corner towers.
  {
    span: [2.8, 2.8],
    vents: [[-0.6, -0.5], [0.6, 0.4], [0, -0.2]],
    over(c, e, r, t, phase) {
      glow(c, 0, 0, r * (0.42 + 0.06 * Math.sin(t * 0.15)), phase, 0.9);
      for (const [x, y] of [[-0.95, -0.95], [0.95, -0.95], [-0.95, 0.95], [0.95, 0.95]]) {
        const wave = Math.sin(t * 0.2 + x * 3);
        poly(c, [[x * r, y * r - r * 0.34], [x * r + r * 0.3, y * r - r * 0.4 + wave * 4], [x * r + r * 0.02, y * r - r * 0.22]], DANGER, INK, 1.5);
      }
      for (const x of [-0.45, 0, 0.45]) {
        const recoil = Math.max(0, Math.sin(t * 0.1 + x * 4)) * 4;
        box(c, x * r - r * 0.07, r * 0.8 - recoil, r * 0.14, r * 0.34, r * 0.05, "#384257", INK, 2);
      }
    },
    body(c, r) {
      const oct = Array.from({ length: 8 }, (_, i) => [Math.cos((i / 8) * TAU + Math.PI / 8) * r * 1.12, Math.sin((i / 8) * TAU + Math.PI / 8) * r * 1.12]);
      poly(c, oct.map(([x, y]) => [x + 6, y + 10]), "#8A5140", INK, W);
      poly(c, oct, lit(c, r, "#D08E70"), INK, W);
      for (let i = 0; i < 8; i++) {
        const [ax, ay] = oct[i],
          [bx, by] = oct[(i + 1) % 8];
        for (let k = 1; k < 5; k += 2) {
          const x = ax + ((bx - ax) * k) / 5,
            y = ay + ((by - ay) * k) / 5;
          box(c, x - r * 0.07, y - r * 0.07, r * 0.14, r * 0.14, 2, "#E9B08E", INK, 1.5);
        }
      }
      poly(c, oct.map(([x, y]) => [x * 0.72, y * 0.72]), "#B97A60", "rgba(40,20,20,0.5)", 1.5);
      box(c, -r * 0.46, -r * 0.46, r * 0.92, r * 0.92, r * 0.06, lit(c, r * 0.5, "#E9C2A1"), INK, W);
      poly(c, [[0, -r * 0.46], [r * 0.46, 0], [0, r * 0.46], [-r * 0.46, 0]], "#D9A686", "rgba(80,40,30,0.5)", 1.5);
      disc(c, 0, 0, r * 0.22, "#3A2230", INK, 2.4);
      for (const [x, y] of [[-0.95, -0.95], [0.95, -0.95], [-0.95, 0.95], [0.95, 0.95]]) {
        disc(c, x * r + 4, y * r + 7, r * 0.3, "rgba(40,20,20,0.35)");
        disc(c, x * r, y * r, r * 0.3, "#C77E61", INK, W);
        poly(c, Array.from({ length: 6 }, (_, i) => [x * r + Math.cos((i / 6) * TAU) * r * 0.24, y * r + Math.sin((i / 6) * TAU) * r * 0.24]), "#4F7FA8", INK, 2);
        for (let i = 0; i < 6; i++) poly(c, [[x * r, y * r], [x * r + Math.cos((i / 6) * TAU) * r * 0.24, y * r + Math.sin((i / 6) * TAU) * r * 0.24], [x * r + Math.cos(((i + 1) / 6) * TAU) * r * 0.24, y * r + Math.sin(((i + 1) / 6) * TAU) * r * 0.24]], i % 2 ? "#6FA0C8" : "#3F6A92", null);
        line(c, [[x * r, y * r - r * 0.05], [x * r, y * r - r * 0.4]], "#5A3B22", 2);
      }
    },
  },
  // 3 钟表裁纸机 — brass clockwork with bladed hands and side saws.
  {
    span: [3.6, 2.4],
    vents: [[-0.5, -0.4], [0.5, -0.5], [0, 0.5]],
    under(c, e, r, t) {
      for (const s of [-1, 1]) {
        line(c, [[s * r * 0.8, 0], [s * r * 1.25, r * 0.1]], INK, r * 0.2);
        line(c, [[s * r * 0.8, 0], [s * r * 1.25, r * 0.1]], "#8C6A2E", r * 0.13);
        c.save();
        c.translate(s * r * 1.3, r * 0.12);
        c.rotate(t * 0.3 * s);
        stamp(c, "boss3-saw:" + Math.round(r), r * 1.1, r * 1.1, (g) => {
          g.beginPath();
          for (let i = 0; i < 32; i++) {
            const a = (i / 32) * TAU,
              rr = r * (i % 2 ? 0.44 : 0.52);
            g.lineTo(Math.cos(a) * rr, Math.sin(a) * rr);
          }
          g.closePath();
          g.fillStyle = linear(g, -r * 0.5, -r * 0.5, r * 0.5, r * 0.5, [[0, "#F2F6FA"], [1, "#7D8898"]]);
          g.fill();
          g.strokeStyle = INK;
          g.lineWidth = 2.4;
          g.stroke();
          disc(g, 0, 0, r * 0.14, "#D9A94A", INK, 2);
          for (let i = 0; i < 4; i++) line(g, [[Math.cos((i * TAU) / 4) * r * 0.18, Math.sin((i * TAU) / 4) * r * 0.18], [Math.cos((i * TAU) / 4) * r * 0.38, Math.sin((i * TAU) / 4) * r * 0.38]], "rgba(40,50,70,0.4)", 2);
        });
        c.restore();
      }
    },
    over(c, e, r, t, phase) {
      c.save();
      c.beginPath();
      c.arc(0, r * 0.34, r * 0.28, 0, TAU);
      c.clip();
      for (const [x, y, rr, sp] of [[-0.12, 0.3, 0.18, 1], [0.14, 0.44, 0.14, -1.3]]) {
        c.save();
        c.translate(x * r, y * r);
        c.rotate(t * 0.05 * sp);
        c.beginPath();
        for (let i = 0; i < 20; i++) {
          const a = (i / 20) * TAU,
            k = r * rr * (i % 2 ? 0.8 : 1);
          c.lineTo(Math.cos(a) * k, Math.sin(a) * k);
        }
        c.closePath();
        c.fillStyle = "#C8943A";
        c.fill();
        c.strokeStyle = INK;
        c.lineWidth = 1.5;
        c.stroke();
        c.restore();
      }
      c.restore();
      for (const [len, speed, width] of [[0.72, 0.03, 0.12], [0.5, 0.008, 0.16]]) {
        c.save();
        c.rotate(t * speed);
        poly(c, [[0, r * 0.08], [-r * width * 0.5, -r * len * 0.3], [0, -r * len], [r * width * 0.5, -r * len * 0.3]], linear(c, -r * 0.1, 0, r * 0.1, 0, [[0, "#F7FAFC"], [1, "#8C98A8"]]), INK, 2);
        c.restore();
      }
      disc(c, 0, 0, r * 0.1, phase, INK, 2);
      glow(c, 0, 0, r * 0.3, phase, 0.7);
    },
    body(c, r) {
      disc(c, 0, 0, r * 0.98, lit(c, r, "#D9A94A", 0.3), INK, W);
      disc(c, 0, 0, r * 0.82, "#F6EEDB", INK, 2.4);
      c.strokeStyle = "#3A3326";
      for (let i = 0; i < 60; i++) {
        const a = (i / 60) * TAU,
          long = i % 5 === 0;
        c.lineWidth = long ? 3 : 1.2;
        c.beginPath();
        c.moveTo(Math.cos(a) * r * 0.78, Math.sin(a) * r * 0.78);
        c.lineTo(Math.cos(a) * r * (long ? 0.64 : 0.72), Math.sin(a) * r * (long ? 0.64 : 0.72));
        c.stroke();
      }
      disc(c, 0, r * 0.34, r * 0.3, "#3A2E22", INK, 2.4);
      for (const s of [-1, 1]) {
        box(c, s * r * 0.55 - r * 0.1, -r * 1.12, r * 0.2, r * 0.3, r * 0.05, "#8C6A2E", INK, 2);
        disc(c, s * r * 0.55, -r * 1.14, r * 0.12, "#5A4A34", INK, 2);
      }
      disc(c, 0, -r * 1.05, r * 0.16, "#D9A94A", INK, 2.4);
    },
  },
  // 4 风暴水母 — translucent storm bell crackling with lightning.
  {
    span: [2.6, 2.2],
    vents: [[-0.4, -0.3], [0.4, -0.2]],
    under(c, e, r, t) {
      for (let i = 0; i < 9; i++) {
        const x0 = (i - 4) * r * 0.18,
          pts: number[][] = [];
        for (let k = 0; k <= 10; k++) {
          const a = k / 10;
          pts.push([x0 + Math.sin(t * 0.07 + i + a * 4) * r * 0.25 * a, r * 0.35 + a * r * (1.4 + (i % 3) * 0.25)]);
        }
        line(c, pts, INK, r * 0.07 + 3);
        line(c, pts, i % 2 ? "#5FD8F5" : "#A77BFF", r * 0.07);
        const tip = pts[pts.length - 1];
        glow(c, tip[0], tip[1], r * 0.14, "#9FF4FF", 0.9);
      }
    },
    over(c, e, r, t, phase) {
      c.save();
      c.globalCompositeOperation = "lighter";
      for (let b = 0; b < 3; b++) {
        const seed = Math.floor(t / 6) * 7 + b;
        const pts: number[][] = [[0, -r * 0.35]];
        for (let k = 1; k <= 5; k++) pts.push([(hash(seed, k) - 0.5) * r * 0.9, -r * 0.35 + k * r * 0.13]);
        line(c, pts, "rgba(190,250,255,0.9)", 2.5);
      }
      c.restore();
      glow(c, 0, -r * 0.2, r * (0.55 + 0.08 * Math.sin(t * 0.2)), phase, 0.45);
    },
    body(c, r) {
      c.beginPath();
      c.moveTo(-r * 1.05, r * 0.3);
      c.bezierCurveTo(-r * 1.1, -r * 0.8, r * 1.1, -r * 0.8, r * 1.05, r * 0.3);
      for (let i = 0; i <= 8; i++) {
        const x = r * 1.05 - (i / 8) * r * 2.1;
        c.quadraticCurveTo(x + r * 0.13, r * 0.52, x, r * 0.3);
      }
      c.closePath();
      c.fillStyle = linear(c, 0, -r * 0.8, 0, r * 0.5, [[0, "rgba(150,232,255,0.96)"], [0.6, "rgba(96,112,236,0.92)"], [1, "rgba(150,84,230,0.95)"]]);
      c.fill();
      c.strokeStyle = INK;
      c.lineWidth = W;
      c.stroke();
      c.beginPath();
      c.moveTo(-r * 0.7, r * 0.2);
      c.bezierCurveTo(-r * 0.7, -r * 0.45, r * 0.7, -r * 0.45, r * 0.7, r * 0.2);
      c.strokeStyle = "rgba(255,255,255,0.45)";
      c.lineWidth = 2;
      c.stroke();
      oval(c, -r * 0.4, -r * 0.4, r * 0.3, r * 0.12, "rgba(255,255,255,0.55)", null, 0, -0.4);
      for (const s of [-1, 1]) bigEye(c, s * r * 0.3, r * 0.05, r * 0.14, "#7FE9FF", s * 0.2);
    },
  },
  // 5 蒸汽画鲸 — riveted steam whale with smokestacks.
  {
    span: [2.6, 3.4],
    vents: [[-0.3, -0.6], [0.35, -0.2], [0, 0.4]],
    under(c, e, r, t) {
      const sway = Math.sin(t * 0.06) * 0.25;
      c.save();
      c.translate(0, -r * 1.05);
      c.rotate(sway);
      for (const s of [-1, 1]) poly(c, [[0, 0], [s * r * 0.75, -r * 0.45], [s * r * 0.55, -r * 0.1], [s * r * 0.15, r * 0.05]], lit(c, r, "#44617F"), INK, W);
      c.restore();
      for (const s of [-1, 1]) {
        c.save();
        c.translate(s * r * 0.62, r * 0.25);
        c.rotate(s * (0.5 + Math.sin(t * 0.08) * 0.2));
        oval(c, s * r * 0.25, 0, r * 0.36, r * 0.14, "#3F5A76", INK, 2.4);
        c.restore();
      }
    },
    over(c, e, r, t, phase) {
      for (const s of [-1, 1])
        for (let i = 0; i < 4; i++) {
          const life = ((t * 0.9 + i * 18 + (s > 0 ? 9 : 0)) % 72) / 72,
            x = s * r * 0.24 + Math.sin(life * 5 + s) * 8,
            y = -r * 0.55 - life * r * 0.9;
          disc(c, x, y, r * (0.08 + life * 0.18), `rgba(236,240,246,${0.75 * (1 - life)})`);
        }
      for (let i = 0; i < 2; i++) disc(c, -r * 0.06 + i * r * 0.12, r * 0.3, r * 0.03, phase);
    },
    body(c, r) {
      const hull = () => {
        c.beginPath();
        c.moveTo(0, -r * 1.2);
        c.bezierCurveTo(r * 0.5, -r * 1.1, r * 0.82, -r * 0.1, r * 0.72, r * 0.6);
        c.bezierCurveTo(r * 0.62, r * 1.1, r * 0.3, r * 1.22, 0, r * 1.22);
        c.bezierCurveTo(-r * 0.3, r * 1.22, -r * 0.62, r * 1.1, -r * 0.72, r * 0.6);
        c.bezierCurveTo(-r * 0.82, -r * 0.1, -r * 0.5, -r * 1.1, 0, -r * 1.2);
        c.closePath();
      };
      hull();
      c.fillStyle = linear(c, -r, -r, r, r, [[0, "#5D7FA6"], [0.5, "#3C5A7E"], [1, "#223752"]]);
      c.fill();
      c.strokeStyle = INK;
      c.lineWidth = W;
      c.stroke();
      c.save();
      hull();
      c.clip();
      oval(c, -r * 0.2, -r * 0.1, r * 0.25, r * 0.9, "rgba(255,255,255,0.12)");
      for (const y of [-0.62, -0.05, 0.5]) {
        box(c, -r, y * r - r * 0.075, r * 2, r * 0.15, 0, linear(c, 0, y * r - r * 0.08, 0, y * r + r * 0.08, [[0, "#E7BD62"], [1, "#9C7430"]]), INK, 2);
        for (let x = -0.7; x <= 0.7; x += 0.175) disc(c, x * r, y * r, r * 0.028, "#5C4420");
      }
      c.restore();
      for (const s of [-1, 1])
        for (let i = 0; i < 3; i++) {
          disc(c, s * r * 0.52, -r * 0.34 + i * r * 0.28, r * 0.08, "#C69A4A", INK, 1.5);
          disc(c, s * r * 0.52, -r * 0.34 + i * r * 0.28, r * 0.05, "#FFE08A");
        }
      for (const s of [-1, 1]) {
        disc(c, s * r * 0.24, -r * 0.42, r * 0.16, "#C69A4A", INK, 2.4);
        disc(c, s * r * 0.24, -r * 0.42, r * 0.1, "#1E2430");
      }
      for (let i = 0; i < 2; i++) box(c, -r * 0.1 + i * r * 0.12 - r * 0.02, r * 0.18, r * 0.08, r * 0.2, 2, "#2A3444", INK, 1.5);
      for (const s of [-1, 1]) bigEye(c, s * r * 0.4, r * 0.84, r * 0.12, "#FF9A3D", s * 0.3);
      c.beginPath();
      c.moveTo(-r * 0.3, r * 1.06);
      c.quadraticCurveTo(0, r * 1.16, r * 0.3, r * 1.06);
      c.strokeStyle = INK;
      c.lineWidth = 2.4;
      c.stroke();
    },
  },
];

/** Boss art extends past the 74-unit hit radius so the hull reads as huge. */
const ART = 1.28;
const memory = new Map<number, { hp: number; flash: number }>();
export function paintBoss(c: Ctx, e: Enemy, tick: number, shadow = 0.28) {
  const d = BOSSES[((e.kind % 6) + 6) % 6],
    r = e.radius,
    t = e.age,
    phase = PHASE[Math.floor(t / 300) % 3],
    w = d.span[0] * r,
    h = d.span[1] * r,
    key = `boss:${e.kind % 6}:${Math.round(r)}`;
  const m = memory.get(e.id) ?? { hp: e.hp, flash: -99 };
  if (e.hp < m.hp - 0.01) m.flash = tick;
  m.hp = e.hp;
  memory.set(e.id, m);
  if (memory.size > 8) memory.delete(memory.keys().next().value!);
  c.save();
  c.translate(e.x, e.y + Math.sin(t * 0.04) * 4);
  c.scale(ART, ART);
  if (shadow > 0) {
    c.globalAlpha = shadow;
    silhouette(c, key, w, h, (g) => d.body(g, r), "#0A1020", r * 0.4, r * 0.9, 0, 0.92);
    c.globalAlpha = 1;
  }
  if (d.under) d.under(c, e, r, t, phase);
  stamp(c, key, w, h, (g) => d.body(g, r));
  if (d.over) d.over(c, e, r, t, phase);
  if (tick - m.flash < 4) {
    c.globalAlpha = 0.5 * (1 - (tick - m.flash) / 4);
    silhouette(c, key, w, h, (g) => d.body(g, r), "#FFFFFF");
    c.globalAlpha = 1;
  }
  // Damage: smoke from the hull below half health, flames below a quarter.
  const hurt = 1 - clamp(e.hp / e.maxHp, 0, 1);
  if (hurt > 0.5)
    for (const [vx, vy] of d.vents) {
      for (let i = 0; i < 4; i++) {
        const life = ((t + i * 11 + vx * 50) % 44) / 44;
        disc(c, vx * r + Math.sin(life * 6 + i) * 6, vy * r + life * r * 0.9, 5 + life * 14, `rgba(50,50,60,${0.5 * (1 - life)})`);
      }
      if (hurt > 0.75) glow(c, vx * r, vy * r, r * (0.16 + 0.05 * Math.sin(t * 0.9 + vx)), "#FF7A2E", 1);
    }
  c.restore();
}
